import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  useEmployee,
  useEmployeeImage,
  useCreateEmployee,
  useUpdateEmployee,
  useUploadEmployeeImage,
  useCheckCompleteness,
} from "../../../api/queries/employeeQueries";
import {
  useCompanies,
  useBranchesByCompany,
  useDepartments,
  useJobTitles,
  useClients,
} from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { SectionToolbar, EditActions, SubHeader } from "./ProfileUI";
import { EMPLOYEE_STATUSES } from "../../../utils/employeeStatus";
import { useEmployeeIdPreview } from "../../../api/queries/adminToolsQueries";
import { ID_SOURCE } from "../../admin/EmployeeIdFormatPage";

const Section = ({ title, children, icon }) => (
  <div className="mb-4">
    <SubHeader title={title} icon={icon} />
    <div className="row g-3">{children}</div>
  </div>
);

const CustomInput = ({
  label,
  name,
  value,
  onChange,
  disabled,
  type = "text",
  col = "4",
  required = false,
  error = false,
  readOnly = false,
}) => (
  <div className={`col-md-${col}`}>
    <label className="profile-label">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      className={`form-control form-control-sm shadow-none ${error ? "border-danger" : ""} field-control ${disabled ? "is-readonly" : readOnly ? "is-locked" : ""} ${!disabled && error ? "has-error" : ""}`}
      disabled={disabled}
      readOnly={readOnly}
    />
  </div>
);

// Stable empty list so effects that depend on lookup data don't re-run every render.
const EMPTY = [];
const PLACEHOLDER_AVATAR = "https://placehold.co/200x200?text=Avatar";

const formatDate = (date) =>
  date && date !== "string" ? date.slice(0, 10) : "";

const EMPTY_FORM = {
  firstName: "",
  middleName: "",
  lastName: "",
  extension: "",
  employeeId: "",
  employeeStatus: "Active",
  employeeLeaveStatus: "Available",
  companyEmail: "",
  personalEmail: "",
  companyId: "",
  branchId: "",
  departmentId: "",
  jobTitle: "",
  clientCode: "",
  hiringDate: "",
  resignationDate: "",
  separationDate: "",
  terminationDate: "",
  probationaryDate: "",
  regularizationDate: "",
  remarks: "",
  assignmentType: "Internal",
  clientId: "",
  isSeasonal: false,
};

/** Employee record from the API -> form values. */
const employeeToForm = (employee, jobTitlesList) => {
  const matchedJobTitle = jobTitlesList.find(
    (jt) => jt.id === employee.jobTitleId,
  );
  return {
    ...employee,
    firstName: employee.firstName ?? "",
    middleName:
      employee.middleName === "string" || !employee.middleName
        ? ""
        : employee.middleName,
    lastName: employee.lastName ?? "",
    extension:
      employee.extension === "string" || !employee.extension
        ? ""
        : employee.extension,
    personalEmail: employee.personalEmail ?? "",
    companyEmail: employee.companyEmail ?? "",
    companyId: employee.companyId ?? "",
    branchId: employee.branchId ?? "",
    departmentId: employee.departmentId ?? "",
    clientId: employee.clientId ?? "",
    jobTitle: matchedJobTitle ? matchedJobTitle.title : "",
    clientCode: employee.client?.clientCode || "",
    hiringDate: formatDate(employee.hiringDate),
    resignationDate: formatDate(employee.resignationDate),
    separationDate: formatDate(employee.separationDate),
    terminationDate: formatDate(employee.terminationDate),
    probationaryDate: formatDate(employee.probationaryDate),
    regularizationDate: formatDate(employee.regularizationDate),
    remarks: employee.remarks === "string" ? "" : (employee.remarks ?? ""),
    assignmentType: employee.assignmentType ?? "Internal",
    employeeStatus: employee.employeeStatus ?? "Active",
    employeeLeaveStatus: employee.employeeLeaveStatus ?? "Available",
    isSeasonal: employee.isSeasonal === true,
  };
};

const EmployeeDetails = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const hasLoadedRef = useRef(false);

  const [isEditing, setIsEditing] = useState(!id);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [preview, setPreview] = useState(PLACEHOLDER_AVATAR);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Server data (shared with the other profile sections through the query cache)
  const { data: employee } = useEmployee(id);
  const { data: image } = useEmployeeImage(id);
  const { data: allOrgs = EMPTY } = useCompanies();
  const { data: allBranches = EMPTY } = useBranchesByCompany(form.companyId);
  const { data: departments = EMPTY } = useDepartments(form.companyId, {
    enabled: !!form.companyId,
  });
  const { data: jobTitlesList = EMPTY } = useJobTitles();
  const { data: clientList = EMPTY } = useClients();
  // New employee: the ID the server will give (client format for deployed staff); preview only
  const idPreview = useEmployeeIdPreview(
    form.companyId,
    {
      clientId:
        form.assignmentType === "Deployed"
          ? form.clientId || undefined
          : undefined,
      hiringDate: form.hiringDate || undefined,
    },
    { enabled: !id },
  );

  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();
  const uploadImage = useUploadEmployeeImage();
  const checkCompleteness = useCheckCompleteness();

  // Field classes for view / edit / error states (see profile.css)
  const fieldClass = (name) =>
    `field-control ${!isEditing ? "is-readonly" : ""} ${isEditing && errors[name] ? "has-error" : ""}`;

  // Copy the server record into the form whenever it (or the job title list) changes.
  useEffect(() => {
    if (employee) {
      setForm(employeeToForm(employee, jobTitlesList));
      if (id && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        setIsEditing(false);
      }
    } else if (!id) {
      setForm(EMPTY_FORM);
      setIsEditing(true);
    }
  }, [employee, id, jobTitlesList]);

  useEffect(() => {
    if (image?.imageUrl && !selectedFile) setPreview(image.imageUrl);
  }, [image, selectedFile]);

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const validate = () => {
    let newErrors = {};
    if (!form.firstName) newErrors.firstName = true;
    if (!form.lastName) newErrors.lastName = true;
    if (!form.personalEmail) newErrors.personalEmail = true;
    if (!form.companyId) newErrors.companyId = true;
    if (!form.branchId) newErrors.branchId = true;
    if (!form.jobTitle) newErrors.jobTitle = true;
    if (!form.hiringDate) newErrors.hiringDate = true;

    if (form.assignmentType === "Internal") {
      if (!form.departmentId) newErrors.departmentId = true;
    } else {
      if (!form.clientId) newErrors.clientId = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStatusMsg({ text: "", type: "" });
    setForm((prev) => {
      let updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
        ...(name === "companyId" ? { departmentId: "", branchId: "" } : {}),
      };
      if (name === "clientId") {
        const client = clientList.find((c) => String(c.id) === String(value));
        updated.clientCode = client ? client.clientCode : "";
      }
      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      setStatusMsg({ text: "Missing required fields.", type: "danger" });
      return;
    }

    setIsSaving(true);
    setStatusMsg({ text: "", type: "" });

    const payload = {
      firstName: form.firstName,
      middleName: form.middleName || "",
      lastName: form.lastName,
      extension: form.extension || "",
      companyEmail: form.companyEmail || "",
      personalEmail: form.personalEmail,
      companyId: form.companyId,
      branchId: form.branchId,
      assignmentType: form.assignmentType,
      departmentId:
        form.assignmentType === "Internal" ? form.departmentId : null,
      clientId: form.assignmentType === "Deployed" ? form.clientId : null,
      isSeasonal: form.assignmentType === "Deployed" ? form.isSeasonal : false,
      jobTitleId:
        jobTitlesList.find((jt) => jt.title === form.jobTitle)?.id || null,
      hiringDate: form.hiringDate,
      probationaryDate: form.probationaryDate ? form.probationaryDate : null,
      regularizationDate: form.regularizationDate
        ? form.regularizationDate
        : null,
      resignationDate: form.resignationDate ? form.resignationDate : null,
      separationDate: form.separationDate ? form.separationDate : null,
      terminationDate: form.terminationDate ? form.terminationDate : null,
      remarks: form.remarks || "",
      employeeStatus: form.employeeStatus,
    };

    try {
      let result;
      let targetId = id;

      if (id) {
        result = await updateEmployee.mutateAsync({ id, data: payload });
      } else {
        result = await createEmployee.mutateAsync(payload);
        targetId = result.id;
        hasLoadedRef.current = false;
      }

      if (selectedFile) {
        const imageData = new FormData();
        imageData.append("file", selectedFile);
        await uploadImage.mutateAsync({
          employeeId: targetId,
          file: imageData,
        });
        setSelectedFile(null);
      }

      // Recalculates the status; the employee record then refreshes automatically
      await checkCompleteness.mutateAsync(targetId);

      setStatusMsg({
        text: "Saved and validated successfully!",
        type: "success",
      });
      setIsEditing(false);

      if (!id && targetId) {
        navigate(`/employee/${targetId}`, { replace: true });
      }

      setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    } catch (err) {
      setStatusMsg({
        text: getApiErrorMessage(err, "Error saving."),
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const badgeClass = (() => {
    const s = (form.employeeStatus || "").toLowerCase();
    if (s === "active" || s === "complete")
      return "status-badge-modern--success";
    if (s === "incomplete" || s === "inactive")
      return "status-badge-modern--danger";
    return "";
  })();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleCancel = () => {
    if (!id) navigate("/employee");
    else {
      setIsEditing(false);
      setSelectedFile(null);
      hasLoadedRef.current = false;
      setErrors({});
      setStatusMsg({ text: "", type: "" });
      // Discard edits: reset the form and photo from the cached server data
      if (employee) setForm(employeeToForm(employee, jobTitlesList));
      setPreview(image?.imageUrl || PLACEHOLDER_AVATAR);
    }
  };

  return (
    <div className="position-relative">
      <div>
        {isSaving && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <SectionToolbar statusMsg={statusMsg}>
          <EditActions
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onSave={handleSubmit}
            onCancel={handleCancel}
            isSaving={isSaving}
          />
        </SectionToolbar>

        <form onSubmit={handleSubmit}>
          <div className="row g-0">
            <div className="col-12 col-lg-3 profile-photo-col mb-4">
              <div className="avatar-container mb-3">
                <img src={preview} alt="Avatar" className="avatar-circle" />
                {isEditing && (
                  <div
                    className="upload-badge"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <i className="bi bi-camera-fill"></i>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handlePhotoChange}
              />

              <div className="mb-4">
                {!isEditing ? (
                  <div className={`status-badge-modern ${badgeClass}`}>
                    {form.employeeStatus}
                  </div>
                ) : (
                  <div className="text-start px-1">
                    <label className="profile-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      name="employeeStatus"
                      className={`form-select form-select-sm shadow-none ${fieldClass("employeeStatus")}`}
                      value={form.employeeStatus}
                      onChange={handleChange}
                    >
                      {EMPLOYEE_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className={`status-badge-modern mb-3 ${badgeClass}`}>
                {form.employeeLeaveStatus || "Available"}
              </div>

              <div className="text-start mb-3 px-1">
                <label className="profile-label">Employee ID</label>
                <div className="form-control border-0 shadow-none text-center fw-bold employee-id-box">
                  {form.employeeId ? (
                    <>
                      ID: {form.employeeId} - {form.branch?.BranchCode || "HQ"}
                    </>
                  ) : (
                    "NEW RECORD"
                  )}
                </div>
                {!form.employeeId && idPreview.data?.preview && (
                  <div
                    className="fs-075 text-slate-600 text-center mt-1"
                    title="Preview only: the ID is assigned when you save"
                  >
                    Next ID:{" "}
                    <strong className="font-monospace">
                      {idPreview.data.preview}
                    </strong>
                    {idPreview.data.source
                      ? ` (${ID_SOURCE[idPreview.data.source] || idPreview.data.source})`
                      : ""}
                  </div>
                )}
              </div>

              <div className="text-start mb-1 px-1">
                <label className="profile-label">
                  Assignment Type <span className="text-danger">*</span>
                </label>
                <select
                  name="assignmentType"
                  className={`form-select form-select-sm shadow-none ${fieldClass("assignmentType")}`}
                  value={form.assignmentType}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  <option value="Internal">Internal</option>
                  <option value="Deployed">Deployed</option>
                </select>
              </div>

              {form.assignmentType === "Deployed" && (
                <div className="text-start mb-3 px-1 d-flex align-items-center mt-2">
                  <input
                    type="checkbox"
                    id="isSeasonal"
                    name="isSeasonal"
                    className="form-check-input me-2"
                    checked={form.isSeasonal}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                  <label
                    htmlFor="isSeasonal"
                    className="extra-small text-muted text-uppercase m-0"
                  >
                    Seasonal Employee
                  </label>
                </div>
              )}
            </div>

            <div className="col-12 col-lg-9 profile-fields-col">
              <Section title="Personal Information" icon="bi-person-circle">
                <CustomInput
                  label="First Name"
                  name="firstName"
                  required
                  value={form.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.firstName}
                />
                <CustomInput
                  label="Middle Name"
                  name="middleName"
                  col="3"
                  value={form.middleName}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Last Name"
                  name="lastName"
                  col="3"
                  required
                  value={form.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.lastName}
                />
                <CustomInput
                  label="Extension"
                  name="extension"
                  col="2"
                  value={form.extension}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Personal Email"
                  name="personalEmail"
                  col="6"
                  required
                  value={form.personalEmail}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.personalEmail}
                />
              </Section>

              <Section title="Employment Information" icon="bi-building">
                <div className="col-md-6">
                  <label className="profile-label">
                    Company <span className="text-danger">*</span>
                  </label>
                  <select
                    name="companyId"
                    className={`form-select form-select-sm ${errors.companyId ? "border-danger" : ""} ${fieldClass("companyId")}`}
                    value={form.companyId}
                    onChange={handleChange}
                    disabled={!isEditing}
                  >
                    <option value="">Select Company</option>
                    {allOrgs.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="profile-label">
                    Branch <span className="text-danger">*</span>
                  </label>
                  <select
                    name="branchId"
                    className={`form-select form-select-sm ${errors.branchId ? "border-danger" : ""} ${fieldClass("branchId")}`}
                    value={form.branchId}
                    onChange={handleChange}
                    disabled={!isEditing}
                  >
                    <option value="">Select Branch</option>
                    {allBranches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branchName || b.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                {form.assignmentType === "Internal" ? (
                  <>
                    <div className="col-md-6">
                      <label className="profile-label">
                        Department <span className="text-danger">*</span>
                      </label>
                      <select
                        name="departmentId"
                        className={`form-select form-select-sm ${errors.departmentId ? "border-danger" : ""} ${fieldClass("departmentId")}`}
                        value={form.departmentId}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Department</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.departmentName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="profile-label">
                        Job Title <span className="text-danger">*</span>
                      </label>
                      <select
                        name="jobTitle"
                        className={`form-select form-select-sm ${errors.jobTitle ? "border-danger" : ""} ${fieldClass("jobTitle")}`}
                        value={form.jobTitle}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Job Title</option>
                        {jobTitlesList.map((jt) => (
                          <option key={jt.id} value={jt.title}>
                            {jt.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <CustomInput
                      label="Company Email"
                      name="companyEmail"
                      col="6"
                      value={form.companyEmail}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </>
                ) : (
                  <>
                    <div className="col-md-6">
                      <label className="profile-label">
                        Payroll Group <span className="text-danger">*</span>
                      </label>
                      <select
                        name="clientId"
                        className={`form-select form-select-sm ${errors.clientId ? "border-danger" : ""} ${fieldClass("clientId")}`}
                        value={form.clientId}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Payroll Group</option>
                        {clientList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.clientName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <CustomInput
                      label="Payroll Group Code"
                      name="clientCode"
                      value={form.clientCode}
                      onChange={handleChange}
                      disabled={!isEditing}
                      readOnly={true}
                      col="6"
                    />

                    <div className="col-md-6">
                      <label className="profile-label">
                        Job Title <span className="text-danger">*</span>
                      </label>
                      <select
                        name="jobTitle"
                        className={`form-select form-select-sm ${errors.jobTitle ? "border-danger" : ""} ${fieldClass("jobTitle")}`}
                        value={form.jobTitle}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Job Title</option>
                        {jobTitlesList.map((jt) => (
                          <option key={jt.id} value={jt.title}>
                            {jt.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <CustomInput
                      label="Company Email"
                      name="companyEmail"
                      col="6"
                      value={form.companyEmail}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </>
                )}
              </Section>

              <Section title="Employment Dates" icon="bi-calendar-event">
                <CustomInput
                  label="Hiring Date"
                  name="hiringDate"
                  type="date"
                  required
                  value={form.hiringDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.hiringDate}
                />
                <CustomInput
                  label="Probationary"
                  name="probationaryDate"
                  type="date"
                  value={form.probationaryDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Regularization"
                  name="regularizationDate"
                  type="date"
                  value={form.regularizationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Resignation"
                  name="resignationDate"
                  type="date"
                  value={form.resignationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Separation"
                  name="separationDate"
                  type="date"
                  value={form.separationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Termination"
                  name="terminationDate"
                  type="date"
                  value={form.terminationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <div className="col-12">
                  <label className="profile-label">Remarks</label>
                  <textarea
                    name="remarks"
                    rows={3}
                    value={form.remarks}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`form-control shadow-none field-control ${!isEditing ? "is-readonly" : ""}`}
                  />
                </div>
              </Section>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default EmployeeDetails;
